import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { addDays, addMonths, format, subYears } from "date-fns";
import { useSnackbar } from "notistack";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useTripTravelersGroupCreate } from "../../trip/network/tripTravelersGroupCreate";
import { useTravelersCreate } from "../../utils/network/travelersCreate";
import { useTripCreate } from "../../trip/network/tripCreate";
import { useShowError } from "../../../functions/useShowError";
import { setLoading } from "../../utils/loading/redux/actions";
import { Circuit } from "../objects/circuit";
import { AppState } from "../../../reducers";
import { getQuotation } from "./quotationService";
import { useCircuitUpdate } from "./circuitUpdate";

type Callback = (data: CircuitCreateData) => Promise<void>

type Options = {
    onTrigger: () => void,
    onSuccess: (circuit: Circuit) => void,
    onError: (error: AxiosError) => void,
    onFinally: () => void
}

export type CircuitCreateData = {
    contract?: number,
    name: string,
    localization: Omit<Circuit['localization'][number], 'id'>[],
    destination: number,
    duration_day: number,
    duration_night: number,
    type: string,
    reference: string,
    categories: number[],
    currency?: number,
    user_to?: number
}

export function useCircuitCreate(options: Options): Callback {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const quotation_code = useSelector((state: AppState) => state.header.quotation_code);
    const clientId = useSelector((state: AppState) => state.header.client_id);
    const isBeachcomber = quotation_code === 'beachcomber';
    const showError = useShowError();
    const updateCircuit = useCircuitUpdate({});
    const createTravelers = useTravelersCreate({
        onTrigger: () => {
            dispatch(setLoading(true));
        },
        onSuccess: () => {
            // enqueueSnackbar(t('shared.travelers-created'), { variant: 'success' });
        },
        onError: (error) => {
            showError(error);
        },
        onFinally: () => {
            dispatch(setLoading(false));
        }
    });
    const createTravelersGroup = useTripTravelersGroupCreate({
        onTrigger: () => {
            dispatch(setLoading(true));
        },
        onSuccess: () => {
            // enqueueSnackbar(t('shared.circuit-travelers-group-created'), { variant: 'success' });
        },
        onError: (error) => {
            showError(error);
        },
        onFinally: () => {
            dispatch(setLoading(false));
        }
    });
    const createTrip = useTripCreate({
        onTrigger: () => {
            dispatch(setLoading(true));
        },
        onSuccess: () => {
            // enqueueSnackbar(t('shared.circuit-trip-created'), { variant: 'success' });
        },
        onError: (error) => {
            showError(error);
        },
        onFinally: () => {
            dispatch(setLoading(false));
        }
    });

    return async (data) => {
        if (clientId !== null) {
            if(data.duration_day <= 0 || data.duration_night < 0 || !data.duration_day) {
                enqueueSnackbar(t('shared.circuit-duration-error'), { variant: 'error' });
                return;
            }
            try {
                options.onTrigger();
                const response = await makeRequest(clientId, data);
                const travelers = await createTravelers([
                    {
                        first_name: `${t('shared.traveler')} 1`,
                        last_name: `${t('shared.adult')} 1`,
                        birth_date: format(subYears(new Date(), 30) , 'yyyy-MM-dd')
                    },
                    {
                        first_name: `${t('shared.traveler')} 2`,
                        last_name: `${t('shared.adult')} 2`,
                        birth_date: format(subYears(new Date(), 30) , 'yyyy-MM-dd')
                    }
                ]);
                const tripStartDate = addMonths(new Date(), 3);
                // const destination_quotation = await getQuotation(Number(clientId), data.destination);
                const trip = await createTrip({
                    circuit_type: true,
                    circuit: response.data.id,
                    target_client: clientId,
                    currency: data.currency ?? 47,
                    data: {
                        start_date: format(tripStartDate, 'yyyy-MM-dd').concat('T00:00:00Z'),
                        end_date: format(addDays(tripStartDate, data.duration_night), 'yyyy-MM-dd').concat('T23:59:00Z'),
                        destination: data.destination,
                        travelers: travelers.map((item) => item.id as number),
                    },
                    user_to: data.user_to,
                    is_by_day_view: true
                });
                if (trip) {
                    await createTravelersGroup(trip, travelers.map((item) => item.id as number));
                }
                // un circuit doit toujours avoir un variant_name par défaut
                if (trip?.data?.[0]?.id) {
                    await updateCircuit(response.data.id, {
                        variant_name: [{
                            id: 1,
                            version: trip.data[0].id,
                            name: {},
                            reference: '',
                            min_pax_to_validate_trip: null,
                            ...(!isBeachcomber ? {
                                visibility: 'PUBLIC',
                                allowed_cms: []
                            } : {})
                        }]
                    });
                }
                options.onSuccess(response.data);
            } catch (error: any) {
                options.onError(error);
            } finally {
                options.onFinally();
            }
        }
    };
}

function makeRequest(
    clientId: number,
    data: CircuitCreateData
): Promise<AxiosResponse<Circuit>> {
    // user_to est porté par le trip (envoyé via createTrip), pas par le circuit → on l'exclut du POST circuit
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { user_to, userTO, ...circuitData } = data as CircuitCreateData & { userTO?: number | null };
    return axios.post(
        `${API_HREF}client/${clientId}/circuits/`,
        {
            ...circuitData,
            contracts: data.contract ? [data.contract] : [],
        },
        {
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`,
            },
        }
    );
}